import { IS_CLINIC, LABELS } from "../../platform/config";
import { ExportProgressReporter } from "../../components/CustomButton";
import React from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import { countries } from "../../assets/utils/countries";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import Actions from "../../components/Actions";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomUpload from "../../components/CustomUpload";
import CustomLabel from "../../components/CustomLabel";
import PageHeader from "../../components/PageHeader";
import CustomButton from "../../components/CustomButton";
import CustomDialog from "../../components/CustomDialog";
import CustomTextField from "../../components/CustomTextField";
import InvoiceItemsEditor, {
  InvoiceItem,
  isInvoiceProductMissingSelection,
} from "../../components/invoices/InvoiceItemsEditor";
import { getAllBranches } from "../../services/BranchesService";
import {
  downloadClientsCsv,
  getClientsPage,
  createClient,
  updateClient,
  deleteClient,
  banClient,
  unbanClient,
} from "../../services/ClientsService";
import {
  getAllInvoicesForUser,
  getInvoiceById,
  updateInvoice,
  createInvoice,
  deleteInvoice,
} from "../../services/InvoicesService";
import { getAllServices } from "../../services/ServicesService";
import { getUserId } from "../../services/authService";
import {
  createAttachment,
  getClientAttachments,
  deleteAttachment,
  getAttachmentFileUrl,
  downloadAttachmentFile,
} from "../../services/AttachmentsService";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { formatTableDate } from "../../utils/tableDateFormat";
import AddClientDialog from "../../components/AddClientDialog";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  getClientLoyaltyBalance,
  getClientLoyaltyTransactions,
} from "../../services/LoyaltyService";

const calculateItemTotalPrice = (
  quantity: number,
  unitCost: number,
  discount: number
) => {
  const qty = Number(quantity) || 0;
  const unit = Number(unitCost) || 0;
  const discountPercent = Math.min(100, Math.max(0, Number(discount) || 0));
  const subtotal = qty * unit;
  const discountAmount = (subtotal * discountPercent) / 100;
  return Math.round((subtotal - discountAmount) * 100) / 100;
};

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return undefined;
};

const resolveStatusFormValue = (
  statusValue?: unknown,
  statusId?: unknown
): "paid" | "unpaid" | "pending" => {
  const normalize = (
    value: unknown
  ): "paid" | "unpaid" | "pending" | undefined => {
    if (typeof value === "number") {
      if (value === 1) return "paid";
      if (value === 2) return "unpaid";
      if (value === 3) return "pending";
      return undefined;
    }

    if (typeof value === "string") {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return normalize(numeric);
      }
      const normalized = value.trim().toLowerCase();
      if (
        normalized === "paid" ||
        normalized === "unpaid" ||
        normalized === "pending"
      ) {
        return normalized;
      }
    }

    return undefined;
  };

  if (statusValue && typeof statusValue === "object") {
    const statusObject = statusValue as Record<string, unknown>;
    return (
      normalize(
        statusObject.value ||
          statusObject.code ||
          statusObject.status ||
          statusObject.name ||
          statusObject.label ||
          statusObject.title
      ) ||
      normalize(
        statusObject.id ||
          statusObject.status_id ||
          statusObject.value ||
          statusObject.code
      ) ||
      normalize(statusId) ||
      "unpaid"
    );
  }

  return normalize(statusValue) || normalize(statusId) || "unpaid";
};

const extractFileNameFromPath = (value?: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const segments = trimmed.split("/");
  const candidate = segments[segments.length - 1];
  return candidate || undefined;
};

const formatDateOnly = (value: any): string => {
  if (!value) return "";
  const formatted = formatTableDate(value);
  return formatted === "-" ? String(value) : formatted;
};

const toDateInputValue = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
    return "";
  }
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  const parsed = new Date(value);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return "";
};

const getAttachmentDisplayName = (attachment?: {
  file?: unknown;
  file_name?: unknown;
  title?: unknown;
}): string => {
  if (!attachment) return "attachment";

  if (typeof attachment.file_name === "string" && attachment.file_name.trim()) {
    return attachment.file_name.trim();
  }

  const derived = extractFileNameFromPath(attachment.file);
  if (derived) {
    return derived;
  }

  if (typeof attachment.title === "string" && attachment.title.trim()) {
    return attachment.title.trim();
  }

  return "attachment";
};

const triggerAttachmentDownload = async (attachment: any) => {
  if (
    !attachment?.id ||
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return false;
  }

  try {
    const response = await downloadAttachmentFile(String(attachment.id));
    const url = window.URL.createObjectURL(response.blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = response.fileName || getAttachmentDisplayName(attachment);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error("Failed to download attachment via API:", error);
    return false;
  }
};

const openAttachmentInNewTab = (attachment?: any) => {
  if (typeof window === "undefined") return false;
  const fileUrl = getAttachmentFileUrl(attachment);
  if (!fileUrl) return false;
  const opened = window.open(fileUrl, "_blank", "noopener,noreferrer");
  if (!opened) {
    toast.error("Unable to open file. Please allow pop-ups and try again.");
    return false;
  }
  return true;
};

const resolvePaymentMethodFormValue = (
  entry?: any
): "cash" | "card" | "bank transfer" => {
  const candidate =
    typeof entry === "object" && entry !== null
      ? entry.payment_method_id ??
        entry.payment_method?.id ??
        entry.invoice?.payment_method_id ??
        entry.invoice?.payment_method?.id ??
        entry.payment_method ??
        entry.invoice?.payment_method ??
        entry.method ??
        entry.invoice?.method
      : entry;

  const numeric = Number(candidate);
  if (!Number.isNaN(numeric)) {
    if (numeric === 1) return "cash";
    if (numeric === 2) return "card";
    if (numeric === 3) return "bank transfer";
  }

  if (typeof candidate === "string") {
    const normalized = candidate.trim().toLowerCase();
    if (normalized === "cash") return "cash";
    if (normalized === "card") return "card";
    if (
      normalized === "bank transfer" ||
      normalized === "bank_transfer" ||
      normalized === "bank-transfer"
    ) {
      return "bank transfer";
    }
  }

  return "cash";
};

const statusStringToId = (value: "paid" | "unpaid" | "pending"): 1 | 2 | 3 => {
  if (value === "paid") return 1;
  if (value === "unpaid") return 2;
  return 3;
};

const paymentMethodStringToId = (
  value: "cash" | "card" | "bank transfer"
): 1 | 2 | 3 => {
  if (value === "card") return 2;
  if (value === "bank transfer") return 3;
  return 1;
};

const normalizeCountryCodeValue = (value?: unknown): string => {
  if (value === undefined || value === null) return "";
  const stringValue = typeof value === "string" ? value : String(value);
  const match = stringValue.match(/^\s*(\+?\d+)/);
  const dialCode = match ? match[1] : stringValue.trim();
  if (!dialCode) return "";
  return dialCode.startsWith("+") ? dialCode : `+${dialCode}`;
};

const CLIENTS_PAGE_SIZE = 30;

const toClientRow = (client: any) => {
  const unitId = client.unit_id || "";
  const unitName = client.unit_name || (unitId ? `Branch ${unitId}` : "N/A");
  const fullName = client.full_name || "";
  const email = client.user?.email || client.email || "";
  const countryCode = normalizeCountryCodeValue(client.country);
  const phone = client.phone || "";
  const genderRaw = client.gender || "";
  const gender = typeof genderRaw === "string" ? genderRaw.toLowerCase() : "";
  const dobRaw =
    client.DOB ||
    client.dob ||
    client.date_of_birth ||
    client.birthdate ||
    "";
  const dob = dobRaw ? formatDateOnly(dobRaw) : "";
  const isBanned = Boolean(client.isBanned ?? client.is_banned);
  return {
    id: client.id || client.user_id || client.user?.id,
    fullName,
    email,
    countryCode,
    phoneNumber: phone,
    unit: unitId || "all",
    unitName,
    isBanned,
    status: isBanned ? "Banned" : client.active ? "Verified" : "Unverified",
    gender,
    dob,
    dobRaw,
  };
};

const FALLBACK_SERVICE_OPTIONS = [60, 90, 120];
const formatAmountString = (value: number | string | undefined) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric.toFixed(2);
  }
  return "0.00";
};

const normalizeInvoiceData = (invoiceData: any) => {
  if (!invoiceData) return invoiceData;

  const client =
    invoiceData.client ||
    invoiceData.client_details ||
    invoiceData.invoice?.client ||
    {};

  const normalizedName =
    pickNonEmptyString(
      invoiceData.name,
      invoiceData.fullName,
      client?.name,
      client?.fullName,
      invoiceData.client_name
    ) || "";

  const normalizedMobile =
    pickNonEmptyString(
      invoiceData.mobile,
      client?.mobile,
      client?.phoneNumber,
      client?.phone,
      invoiceData.client_phone
    ) || "";

  const normalizedCreatedAt =
    invoiceData.created_at ||
    invoiceData.invoice_date ||
    invoiceData.invoiceDate ||
    invoiceData.invoice?.created_at ||
    invoiceData.invoice?.invoice_date ||
    invoiceData.date ||
    invoiceData.updated_at ||
    null;

  const normalizedId =
    invoiceData?.id ??
    invoiceData?.invoice?.id ??
    invoiceData?.invoice_id ??
    (Array.isArray(invoiceData?.details) &&
    invoiceData.details.length > 0 &&
    invoiceData.details[0]
      ? invoiceData.details[0].invoice_id
      : undefined);

  const statusIdCandidate =
    invoiceData?.status_id ??
    invoiceData?.status?.id ??
    invoiceData?.status?.status_id ??
    invoiceData?.invoice?.status_id ??
    invoiceData?.invoice?.status?.id ??
    invoiceData?.invoice?.status?.status_id;

  const normalizedStatus = resolveStatusFormValue(
    invoiceData?.status ||
      invoiceData?.invoice?.status ||
      invoiceData?.invoice?.status_name ||
      invoiceData?.invoice?.status_label,
    statusIdCandidate
  );

  const numericStatusId = Number(statusIdCandidate);

  const paymentMethodCandidate =
    invoiceData?.payment_method_id ??
    invoiceData?.payment_method?.id ??
    invoiceData?.invoice?.payment_method_id ??
    invoiceData?.invoice?.payment_method?.id ??
    invoiceData?.method_id ??
    invoiceData?.paymentMethodId ??
    invoiceData?.invoice?.method_id;

  const numericPaymentMethodId = Number(paymentMethodCandidate);

  return {
    ...invoiceData,
    client,
    id: normalizedId ?? invoiceData?.id ?? null,
    name: normalizedName,
    mobile: normalizedMobile,
    created_at: normalizedCreatedAt,
    status: normalizedStatus,
    status_id: Number.isFinite(numericStatusId)
      ? Number(numericStatusId)
      : statusStringToId(normalizedStatus),
    payment_method_id: Number.isFinite(numericPaymentMethodId)
      ? Number(numericPaymentMethodId)
      : paymentMethodStringToId(resolvePaymentMethodFormValue(invoiceData)),
  };
};

interface ClientsProps {
  /** If true the "Add Client" dialog will open automatically on mount/update */
  openAddClient?: boolean;
}

interface ClientLoyaltyModalState {
  open: boolean;
  client: any | null;
  loading: boolean;
  error: string;
  balance: number;
  transactions: any[];
}

const Clients: React.FC<ClientsProps> = ({ openAddClient = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  //const businessId = location.state?.businessId || params.businessId || "";

  const params = useParams<{ businessId?: string; id?: string }>();
const businessId = params.businessId || params.id;

if (!businessId) {
  throw new Error("Clients page requires businessId in route");
}

const [addClientDialogOpen, setAddClientDialogOpen] = React.useState(false);
const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );
  const [banConfirmOpen, setBanConfirmOpen] = React.useState(false);
  const [banTarget, setBanTarget] = React.useState<any | null>(null);
  const [importOpen, setImportOpen] = React.useState(false);
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);
  const [uploadInputKey, setUploadInputKey] = React.useState(0);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = React.useState(false);
  const [attachments, setAttachments] = React.useState<File[]>([]);
  const [attachmentTitle, setAttachmentTitle] = React.useState("");
  const [attachmentNotes, setAttachmentNotes] = React.useState("");
  const [attachmentUploadKey, setAttachmentUploadKey] = React.useState(0);
  const [existingAttachments, setExistingAttachments] = React.useState<any[]>(
    []
  );
  const [loadingAttachments, setLoadingAttachments] = React.useState(false);
  const [invoices, setInvoices] = React.useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = React.useState(false);
  const [invoiceEditOpen, setInvoiceEditOpen] = React.useState(false);
  const [editingInvoice, setEditingInvoice] = React.useState<any | null>(null);
  const [invoiceDeleteConfirmOpen, setInvoiceDeleteConfirmOpen] =
    React.useState(false);
  const [invoiceToDeleteId, setInvoiceToDeleteId] = React.useState<
    string | number | null
  >(null);
  const [tableDialogOpen, setTableDialogOpen] = React.useState(false);
  const [invoiceItems, setInvoiceItems] = React.useState<InvoiceItem[]>([]);
  const [services, setServices] = React.useState<any[]>([]);
  const [discount, setDiscount] = React.useState(0);
  const [vat, setVat] = React.useState(0);

  const invoiceTotals = React.useMemo(() => {
    const totals = invoices.reduce(
      (acc, inv) => {
        const total = Number(inv?.total) || 0;
        const balance = Number(inv?.balance) || 0;
        const paid = total - balance;
        acc.total += total;
        acc.paid += paid;
        acc.remaining += balance;
        return acc;
      },
      { total: 0, paid: 0, remaining: 0 }
    );

    return {
      total: totals.total.toFixed(2),
      paid: totals.paid.toFixed(2),
      remaining: totals.remaining.toFixed(2),
    };
  }, [invoices]);

  const [search, setSearch] = React.useState<string>("");
  const [branches, setBranches] = React.useState<any[]>([]);
  const [loadingBranches, setLoadingBranches] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState<any[]>([]);
  const [importedRows, setImportedRows] = React.useState<any[]>([]);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);
  const [clientLoyaltyModal, setClientLoyaltyModal] =
    React.useState<ClientLoyaltyModalState>({
      open: false,
      client: null,
      loading: false,
      error: "",
      balance: 0,
      transactions: [],
    });
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("all");

  const resetUploadedFile = React.useCallback(
    (updateFormField?: (value: any) => void) => {
      setUploadedFile(null);
      setUploadInputKey((prev) => prev + 1);
      if (updateFormField) {
        updateFormField("");
      }
    },
    []
  );

  const resetAttachmentUploadInput = React.useCallback(() => {
    setAttachmentUploadKey((prev) => prev + 1);
  }, []);

  const patchEditingInvoice = React.useCallback((patch: Partial<any>) => {
    setEditingInvoice((prev: any) => (prev ? { ...prev, ...patch } : prev));
  }, []);
  const resolveServiceIdForItem = React.useCallback(
    (item: InvoiceItem): string => {
      if (item.serviceId) {
        return String(item.serviceId);
      }

      const numericService = Number(
        typeof item.service === "number" ? item.service : Number(item.service)
      );
      if (Number.isFinite(numericService) && services.length > 0) {
        const matchedService = services.find((svc: any) => {
          const numericTitle = Number(svc.title);
          return !Number.isNaN(numericTitle) && numericTitle === numericService;
        });
        if (matchedService?.id) {
          return String(matchedService.id);
        }
      }

      return String(
        item.serviceId ||
          item.service ||
          FALLBACK_SERVICE_OPTIONS[0] ||
          `service-${item.id}`
      );
    },
    [services]
  );
  const fetchInvoicesForClient = React.useCallback(
    async (clientId?: string | number | null) => {
      if (!clientId) return;
      setLoadingInvoices(true);
      try {
        const response = await getAllInvoicesForUser(String(clientId));
        console.log("Invoices fetched:", response);
        let invoicesData = [];
        if (response && response.invoices) {
          invoicesData = response.invoices;
        } else if (response && response.data) {
          invoicesData = response.data;
        } else if (Array.isArray(response)) {
          invoicesData = response;
        }
        setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
      } catch (error) {
        console.error("Error fetching invoices:", error);
        setInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    },
    []
  );

  React.useEffect(() => {
    if (historyOpen && editingRow?.id) {
      fetchInvoicesForClient(editingRow.id);
    }
  }, [historyOpen, editingRow, fetchInvoicesForClient]);

  const fetchAttachmentsForClient = React.useCallback(
    async (clientId?: string | number | null) => {
      if (!clientId) return;
      setLoadingAttachments(true);
      try {
        const response = await getClientAttachments(String(clientId));
        console.log("Attachments fetched:", response);
        let attachmentsData = [];
        if (response && response.documents) {
          attachmentsData = response.documents;
        } else if (response && response.attachments) {
          attachmentsData = response.attachments;
        } else if (response && response.data) {
          attachmentsData = response.data;
        } else if (Array.isArray(response)) {
          attachmentsData = response;
        }
        setExistingAttachments(
          Array.isArray(attachmentsData) ? attachmentsData : []
        );
      } catch (error) {
        console.error("Error fetching attachments:", error);
        setExistingAttachments([]);
      } finally {
        setLoadingAttachments(false);
      }
    },
    []
  );

  React.useEffect(() => {
    if (attachmentsOpen && editingRow?.id) {
      fetchAttachmentsForClient(editingRow.id);
    }
  }, [attachmentsOpen, editingRow, fetchAttachmentsForClient]);

  React.useEffect(() => {
    const openFromLocation = Boolean(location.state && location.state.openAddClient);
    if (openAddClient || openFromLocation) {
      setEditingRow(null);
      setEditOpen(true);
    }
  }, [openAddClient, location.state]);
console.log('businessId', businessId);
  React.useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        if (!businessId) {
          console.error("No business ID available");
          setBranches([]);
          return;
        }

        const response = await getAllBranches(businessId);
        console.log("Branches fetched:", response);

        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        setBranches(Array.isArray(branchesData) ? branchesData : []);
        console.log("Branches array set:", branchesData);
      } catch (error) {
        console.error("Error fetching branches:", error);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchBranches();
  }, [businessId]);

  const debouncedSearch = useDebouncedValue(search, 400);
  const selectedUnitId =
    selectValue === "all" || !selectValue ? undefined : String(selectValue);
  // "All" in the clinic is every patient of the organization, so someone just
  // added (no appointment yet) can be found straight away. Sports keeps "All"
  // as everyone who booked in this venue.
  const listBusinessId = IS_CLINIC && !selectedUnitId ? "all" : businessId;

  // One page from the server per request; search runs on the server too.
  const fetchClientsPage = React.useCallback(
    async (params?: any) => {
      const page = Number(params?.page) || 0;
      const size = Number(params?.size) || CLIENTS_PAGE_SIZE;
      const empty = { content: [], number: page, size, totalElements: 0, totalPages: 1 };
      if (!businessId || loadingBranches) return empty;
      setLoading(true);
      try {
        const result = await getClientsPage({
          businessId: listBusinessId,
          unitId: selectedUnitId,
          page,
          size,
          search: String(params?.search || ""),
        });
        const mapped = (result?.content ?? []).map(toClientRow);
        // Imported CSV rows were never saved, so they only live here.
        const content = page === 0 ? [...importedRows, ...mapped] : mapped;
        setRows(content);
        return { ...empty, ...result, content };
      } catch (error: any) {
        console.error("Error fetching clients:", error);
        const errorMessage =
          error?.response?.data?.error_description ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to fetch clients.";
        toast.error(errorMessage, {
          position: "top-right",
        });
        setRows([]);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [businessId, listBusinessId, importedRows, loadingBranches, selectedUnitId]
  );


  const openInvoiceEditForId = async (id: string | number) => {
    try {
      const response = await getInvoiceById(String(id));
      const invoiceData = response?.data || response;
      const normalizedInvoice = normalizeInvoiceData(invoiceData);
      const invoiceId =
        normalizedInvoice?.id ||
        invoiceData?.id ||
        invoiceData?.invoice?.id ||
        id;
      setEditingInvoice({
        ...normalizedInvoice,
        id: invoiceId,
      });
      const invoiceBusinessId =
        normalizedInvoice?.business_id ||
        normalizedInvoice?.invoice?.business_id ||
        invoiceData?.business_id ||
        invoiceData?.invoice?.business_id ||
        editingRow?.branch ||
        selectValue ||
        businessId;

      const resolvedDiscount =
        Number(
          normalizedInvoice?.invoice?.discount ??
            normalizedInvoice?.discount ??
            invoiceData?.invoice?.discount ??
            invoiceData?.discount ??
            0
        ) || 0;
      const resolvedVat =
        Number(
          normalizedInvoice?.invoice?.Vat ??
            normalizedInvoice?.Vat ??
            invoiceData?.invoice?.Vat ??
            invoiceData?.Vat ??
            invoiceData?.vat ??
            0
        ) || 0;
      setDiscount(resolvedDiscount);
      setVat(resolvedVat);

      // Initialize invoice items from the invoice data
      const detailSource =
        (normalizedInvoice && normalizedInvoice.details) || invoiceData.details;
      if (detailSource && Array.isArray(detailSource)) {
        const items = detailSource.map((detail: any, index: number) => {
          const serviceLabelRaw =
            pickNonEmptyString(
              detail.serviceLabel,
              detail.service_label,
              detail.service_title,
              detail.service_name,
              detail.title
            ) || "";
          const numericFromLabel = Number(serviceLabelRaw);
          const derivedServiceValue =
            (!Number.isNaN(numericFromLabel) && numericFromLabel > 0
              ? numericFromLabel
              : undefined) ??
            (!Number.isNaN(Number(detail.service))
              ? Number(detail.service)
              : undefined) ??
            (!Number.isNaN(Number(detail.service_id))
              ? Number(detail.service_id)
              : undefined) ??
            FALLBACK_SERVICE_OPTIONS[0];
          const quantityValue = Number(detail.quantity) || 1;
          const unitCostValue =
            Number(detail.amount ?? detail.unit_cost ?? detail.price) || 0;
          const discountValue = Number(detail.discount) || 0;
          const fallbackServiceLabel =
            serviceLabelRaw || String(derivedServiceValue);

          return {
            id: index + 1,
            service: derivedServiceValue,
            serviceId: detail.service_id,
            serviceLabel: fallbackServiceLabel,
            quantity: quantityValue,
            unitCost: unitCostValue,
            discount: discountValue,
            totalPrice:
              Number(detail.net_amount) ||
              calculateItemTotalPrice(
                quantityValue,
                unitCostValue,
                discountValue
              ),
          } as InvoiceItem;
        });
        setInvoiceItems(items);
      } else {
        // Default item if no details
        setInvoiceItems([
          {
            id: 1,
            service: 60,
            serviceLabel: "60",
            quantity: 1,
            unitCost: 40,
            discount: 0,
            totalPrice: calculateItemTotalPrice(1, 40, 0),
          },
        ]);
      }

      setInvoiceEditOpen(true);
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  };

  const actions = Actions({
    handleOpenEditModal: { handler: openInvoiceEditForId },
  });

  const formFields: Field[] = [
    { name: "fullName", label: "Full Name" },
    { name: "email", label: "Email" },
    {
      name: "countryCode",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => ({
        label: `+${c.phone} - ${c.label}`,
        value: `+${c.phone}`,
      })),
      searchable: true,
    },
    { name: "phoneNumber", label: "Phone Number" },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { label: "Verified", value: "Verified" },
        { label: "Unverified", value: "Unverified" },
      ],
    },
    {
      name: "gender",
      label: "Gender",
      type: "dropdown",
      options: [
        { label: "Male", value: "MALE" },
        { label: "Female", value: "FEMALE" },
      ],
    },
    { name: "dob", label: "Date of Birth", type: "date" },
    {
      name: "branch",
      label: "Branch/Business",
      type: "dropdown",
      hidden: true,
      options: branches.map((branch) => ({
        label: branch.name || "Unknown Branch",
        value: branch.id || branch._id || branch.i,
      })),
    },
  ];

  const importFields: Field[] = [
    {
      name: "file",
      label: "Select CSV File",
      customRender: (value, onChange) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <CustomUpload
            key={uploadInputKey}
            label="Upload CSV File"
            accept=".csv"
            onChange={(files) => {
              if (files && files.length > 0) {
                const file = files[0];
                setUploadedFile(file);
                onChange(file.name);
              } else {
                resetUploadedFile(onChange);
              }
            }}
          />

          {uploadedFile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                p: 1.5,
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                backgroundColor: "#fafafa",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                  {uploadedFile.name}
                </Box>
                <Box sx={{ fontSize: "0.8rem", color: "#6b7280" }}>
                  {`${(uploadedFile.size / 1024).toFixed(1)} KB`}
                </Box>
              </Box>
              <CustomButton
                label="Remove"
                variant="outlined"
                size="small"
                startIcon={<DeleteIcon fontSize="small" />}
                onClick={() => resetUploadedFile(onChange)}
              />
            </Box>
          )}
        </Box>
      ),
    },
  ];

  const invoiceFormFields: Field[] = [
    { name: "clientName", label: "Client Name" },
    { name: "email", label: "Email" },
    { name: "phoneNumber", label: "Phone Number" },
    { name: "invoice_date", label: "Invoice Date", type: "date" },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: ["paid", "unpaid", "pending", "cancelled"],
    },
    {
      name: "paymentMethod",
      label: "Payment Method",
      type: "dropdown",
      options: ["cash"],
      disabled: true,
    },
    { name: "notes", label: "Notes" },
    {
      name: "items",
      label: "Items",
      customRender: (value, onChange) => (
        <CustomButton
          label="Edit Items"
          onClick={() => {
            setInvoiceItems(Array.isArray(value) ? value : []);
            setTableDialogOpen(true);
          }}
          bgColor={COLORS.MainColor}
          textColor={COLORS.WhiteText}
        />
      ),
    },
    { name: "invoice_number", label: "Invoice Number" },
    { name: "appointment_id", label: "Appointment ID" },
    { name: "net_amount", label: "Net Amount" },
    { name: "total_amount", label: "Total Amount" },
    { name: "vat", label: "VAT (%)" },
    { name: "discount", label: "Discount (%)" },
  ];

  // Editing happens on the client's own page (Personal Information), the
  // same place a row click leads to.
  const openEditForId = (id: string | number) => {
    navigate(`/dashboard/${businessId}/clients/details/${id}?tab=personal`);
  };
  const openClient = (row: any) => {
    // Imported CSV rows are not saved yet, so they have no page.
    if (!row?.id || String(row.id).startsWith("imported-")) return;
    navigate(`/dashboard/${businessId}/clients/details/${row.id}`);
  };

  const openClientLoyaltyModal = React.useCallback(
    async (clientId: string | number) => {
      const client = rows.find((row: any) => String(row.id) === String(clientId));
      if (!client) {
        toast.error("Client was not found");
        return;
      }

      setClientLoyaltyModal({
        open: true,
        client,
        loading: true,
        error: "",
        balance: 0,
        transactions: [],
      });

      try {
        const [balanceResponse, transactionsResponse] = await Promise.all([
          getClientLoyaltyBalance(String(client.id)),
          getClientLoyaltyTransactions(String(client.id), 3),
        ]);

        setClientLoyaltyModal({
          open: true,
          client,
          loading: false,
          error: "",
          balance: Number(balanceResponse?.balance ?? 0),
          transactions: transactionsResponse,
        });
      } catch (error: any) {
        setClientLoyaltyModal((prev) => ({
          ...prev,
          loading: false,
          error:
            error?.response?.data?.message ||
            error?.message ||
            "Failed to load loyalty details",
        }));
      }
    },
    [rows]
  );

  const closeClientLoyaltyModal = React.useCallback(() => {
    setClientLoyaltyModal((prev) => ({ ...prev, open: false }));
  }, []);

  const wiredActions = [
    ...Actions({
      handleOpenEditModal: { handler: openEditForId },
      onDelete: {
        handler: async (id: number | string) => {
          setToDeleteId(id);
          setConfirmOpen(true);
        },
      },
      onDetails: {
        handler: (id: number | string) => {
          const clientId = String(id);
          navigate(`/dashboard/${businessId}/clients/details/${clientId}`);
        },
      },
    }),
    {
      id: "loyaltyBalance",
      label: "Loyalty",
      name: "Loyalty",
      icon: (
        <Tooltip title="Loyalty">
          <MonetizationOnOutlinedIcon
            sx={{ color: COLORS.MainColor, width: 23 }}
          />
        </Tooltip>
      ),
      handler: (row: any) => {
        openClientLoyaltyModal(row.id);
      },
    },
    {
      id: "banClient",
      label: "Ban client",
      name: "Ban client",
      icon: (row: any) => (
        <Tooltip title={row?.isBanned ? `Unban ${LABELS.client.toLowerCase()}` : `Ban ${LABELS.client.toLowerCase()}`}>
          {row?.isBanned ? (
            <LockOpenIcon sx={{ color: COLORS.MainColor, width: 23 }} />
          ) : (
            <BlockIcon sx={{ color: COLORS.MainColor, width: 23 }} />
          )}
        </Tooltip>
      ),
      handler: (row: any) => {
        setBanTarget(row);
        setBanConfirmOpen(true);
      },
    },
  ];

  const exportCsv = React.useCallback(async (reportProgress?: ExportProgressReporter) => {
    if (!businessId) return;
    try {
      await downloadClientsCsv(
        {
          businessId: listBusinessId,
          unitId: selectedUnitId,
          search: debouncedSearch,
        },
        reportProgress
      );
    } catch (error: any) {
      toast.error(error?.message || "Failed to export clients.");
    }
  }, [businessId, debouncedSearch, selectedUnitId]);

  const importCsv = React.useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (!text) return;
        const lines = text.split("\n").filter((line) => line.trim());
        if (lines.length < 2) return;
        const header = lines[0]
          .split(",")
          .map((h) => h.replace(/"/g, "").trim());
        const dataLines = lines.slice(1);
        const newRows = dataLines.map((line, index) => {
          const values = line.split(",").map((v) => v.replace(/"/g, "").trim());
          const obj: any = {};
          header.forEach((h, i) => {
            obj[h] = values[i] || "";
          });
          // A local-only id; imported rows are not saved to the server.
          obj.id = `imported-${Date.now()}-${index}`;
          obj.gender = obj.gender || "";
          obj.dob = obj.dob || obj.date_of_birth || obj.birthdate || "";
          if (obj.dob) obj.dob = formatDateOnly(obj.dob);
          return obj;
        });
        setImportedRows((prev) => [...newRows, ...prev]);
      };
      reader.readAsText(file);
    },
    []
  );

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title={LABELS.clients}
          subtitle={`Your ${LABELS.client.toLowerCase()} list and their details.`}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          selectConfig={{
            options: [
              { label: "All", value: "all" },
              ...branches.map((branch) => ({
                label: branch.name || "Unknown Branch",
                value: branch.id,
              })),
            ],
            value: selectValue,
            onChange: (v: any) => {
              setSelectValue(v);
            },
            label: IS_CLINIC ? `Select ${LABELS.unit.toLowerCase()}` : "Select Branch",
            placeholder: IS_CLINIC ? `Select ${LABELS.unit.toLowerCase()}` : "Select branch",
            size: "medium",
            searchable: false,
            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          buttonConfig={[
            {
              onClick: () => {
                setAddClientDialogOpen(true);
              },
              startIcon: <AddIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              isIconOnly: true,
              minWidth: 36,
              minHeight: 36,
              borderRadius: "10px",
              sx: {
                width: 36,
                height: 36,
                padding: 0,
                lineHeight: 1,
              },
              "aria-label": "Add client",
              "data-tour": "add-client",
            },
            {
              label: "Import CSV",
              onClick: () => setImportOpen(true),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
            {
              label: "Export CSV",
              onClick: (reportProgress) => exportCsv(reportProgress),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <Box data-tour="client-list">
        <CustomTable<any>
          filters={{
            page: 0,
            size: CLIENTS_PAGE_SIZE,
            search: debouncedSearch,
            unitId: selectedUnitId,
          }}
          refreshSignal={refetchTrigger}
          fields={[
            { key: "fullName", header: "Full Name" },
            {
              key: "email",
              header: "Email",
              transform: async (row: any) => {
                const email = row?.email;
                if (
                  typeof email === "string" &&
                  email.toLowerCase().endsWith("@clinic.com")
                ) {
                  return "_";
                }
                return email ?? "_";
              },
            },
            { key: "gender", header: "Gender" },
            {
              key: "dob",
              header: "Date of Birth",
              transform: (row: any) => formatDateOnly(row?.dob),
            },
            { key: "countryCode", header: "Country Code" },
            { key: "phoneNumber", header: "Phone Number" },
            { key: "status", header: "Status" },
          ]}
          actions={wiredActions}
          fetchFunc={fetchClientsPage}
          onRowClick={openClient}
        />
        </Box>
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit ${LABELS.client}` : `Add ${LABELS.client}`}
          fields={editingRow ? formFields : [...formFields]}
          initialValues={
            editingRow
              ? (() => {
                  const base = Object.keys(editingRow).reduce((acc: any, key) => {
                    const val = editingRow[key];
                    acc[key] =
                      key === "countryCode"
                        ? normalizeCountryCodeValue(val)
                        : key === "gender"
                        ? String(val ?? "").toUpperCase()
                        : key === "dob"
                        ? toDateInputValue(
                            (editingRow as any)?.dobRaw || val || ""
                          )
                        : val === undefined || val === null
                        ? ""
                        : String(val);
                    return acc;
                  }, {});

                  const branchCandidate =
                    base.branch ||
                    editingRow.branch ||
                    editingRow.branchId ||
                    (selectValue && selectValue !== "all" ? selectValue : undefined) ||
                    branches[0]?.business_id ||
                    branches[0]?.id ||
                    branches[0]?._id ||
                    "";

                  return { ...base, branch: branchCandidate };
                })()
              : {
                  status: "Verified",
                  countryCode: "+961",
                  branch:
  branches.length > 0
    ? branches[0].id
    : "",
                }
          }
          additionalActions={
            editingRow
              ? [
                <>
                  <CustomButton
                    key="history"
                    label="History"
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => setHistoryOpen(true)}
                    sx={{ mr: 1 }}
                  />
                  <CustomButton
                    key="attachments"
                    label="Attachments"
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                    onClick={() => setAttachmentsOpen(true)}
                    sx={{ mr: 1 }}
                  />
                </>
                ]
              : []
          }
          onSubmit={async (values) => {
            try {
              // Validate required fields
              if (!values.fullName?.trim()) {
                toast.error("Full name is required", { position: "top-right" });
                return;
              }
              if (!values.phoneNumber?.trim()) {
                toast.error("Phone number is required", { position: "top-right" });
                return;
              }
              if (!values.branch) {
                toast.error("Branch selection is required", { position: "top-right" });
                return;
              }

              const userId = getUserId();
              const payload: any = {
                full_name: values.fullName,
                email: values.email,
                country: values.countryCode,
                phone: values.phoneNumber,
                gender: values.gender ? String(values.gender).toUpperCase() : undefined,
                DOB: values.dob ? new Date(values.dob).toISOString() : undefined,
                active: values.status === "Verified",
                business_id: values.branch,
                user_id: userId,
                admin_user_id: userId,
              };
              delete payload.businessId;

              if (editingRow) {
                await updateClient(editingRow.id, payload);
                toast.success("Client updated successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
                setEditOpen(false);
                setEditingRow(null);
              } else {
                await createClient(payload);
                toast.success("Client created successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
                setEditOpen(false);
              }
            } catch (error: any) {
              console.error("Error saving client:", error);

              let errorMessage = "Failed to save client. Please try again.";

              // Handle different types of errors
              if (error?.response?.status === 400) {
                // Bad request - validation errors
                const errorData = error.response.data;
                if (errorData?.error_description) {
                  errorMessage = errorData.error_description;
                } else if (errorData?.error) {
                  errorMessage = errorData.error;
                } else if (errorData?.message) {
                  errorMessage = errorData.message;
                } else if (typeof errorData === 'string') {
                  errorMessage = errorData;
                } else if (errorData?.errors && Array.isArray(errorData.errors)) {
                  errorMessage = errorData.errors.join(', ');
                }
              } else if (error?.response?.status === 409) {
                // Conflict - duplicate entry
                errorMessage = "A client with this email or phone number already exists.";
              } else if (error?.response?.status === 422) {
                // Unprocessable entity - validation errors
                const errorData = error.response.data;
                if (errorData?.errors) {
                  const fieldErrors = Object.values(errorData.errors).flat();
                  errorMessage = Array.isArray(fieldErrors) ? fieldErrors.join(', ') : String(fieldErrors);
                } else if (errorData?.message) {
                  errorMessage = errorData.message;
                }
              } else if (error?.response?.status >= 500) {
                // Server error
                errorMessage = "Server error occurred. Please try again later.";
              } else if (!error?.response) {
                // Network error
                errorMessage = "Network error. Please check your connection and try again.";
              } else {
                // Fallback error handling
                errorMessage =
                  error?.response?.data?.error_description ||
                  error?.response?.data?.error ||
                  error?.response?.data?.message ||
                  error?.message ||
                  errorMessage;
              }

              toast.error(errorMessage, {
                position: "top-right",
              });
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteClient(String(toDeleteId));
              toast.success("Client deleted successfully!", {
                position: "top-right",
              });
              setRefetchTrigger((prev) => prev + 1);
              setConfirmOpen(false);
              setToDeleteId(null);
            } catch (error: any) {
              console.error("Error deleting client:", error);
              const errorMessage =
                error?.response?.data?.error_description ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to delete client. Please try again.";
              toast.error(errorMessage, {
                position: "top-right",
              });
            }
          }}
        />
        <ConfirmDialog
          open={banConfirmOpen}
          onClose={() => {
            setBanConfirmOpen(false);
            setBanTarget(null);
          }}
          title={banTarget?.isBanned ? `Unban this ${LABELS.client.toLowerCase()}?` : `Ban this ${LABELS.client.toLowerCase()}?`}
          message={
            <Box>
              <Typography>
                {banTarget?.isBanned
                  ? "This client will be able to log in and create an account again using this phone number."
                  : "This client will no longer be able to log in or create a new account using this phone number."}
              </Typography>
              <Typography sx={{ mt: 1, fontWeight: 600 }}>
                Phone: {`${banTarget?.countryCode || ""} ${banTarget?.phoneNumber || ""}`.trim() || "-"}
              </Typography>
            </Box>
          }
          confirmButtonText={banTarget?.isBanned ? "Unban client" : "Ban client"}
          confirmButtonColor={banTarget?.isBanned ? COLORS.MainColor : "#d32f2f"}
          onConfirm={async () => {
            if (!banTarget?.id) return;
            try {
              if (banTarget.isBanned) {
                await unbanClient(String(banTarget.id));
                toast.success("Client unbanned successfully!", {
                  position: "top-right",
                });
              } else {
                await banClient(String(banTarget.id));
                toast.success("Client banned successfully!", {
                  position: "top-right",
                });
              }
              setRefetchTrigger((prev) => prev + 1);
              setBanConfirmOpen(false);
              setBanTarget(null);
            } catch (error: any) {
              const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to update client ban status. Please try again.";
              toast.error(errorMessage, {
                position: "top-right",
              });
            }
          }}
        />
        <Dialog
          open={clientLoyaltyModal.open}
          onClose={closeClientLoyaltyModal}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Loyalty
            {clientLoyaltyModal.client?.fullName
              ? ` - ${clientLoyaltyModal.client.fullName}`
              : ""}
          </DialogTitle>
          <DialogContent>
            {clientLoyaltyModal.loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 180,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            ) : clientLoyaltyModal.error ? (
              <Alert severity="error">{clientLoyaltyModal.error}</Alert>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    border: "1px solid #eee",
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: COLORS.MainBackground,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Current balance
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {clientLoyaltyModal.balance} pts
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Last 3 transactions
                  </Typography>
                  {clientLoyaltyModal.transactions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No loyalty transactions yet.
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {clientLoyaltyModal.transactions.map(
                        (transaction, index) => {
                          const points = Number(transaction?.points ?? 0);
                          const type = String(transaction?.type || "-");
                          const createdAt =
                            transaction?.created_at || transaction?.createdAt;

                          return (
                            <Box
                              key={transaction?.id || `${type}-${index}`}
                              sx={{
                                border: "1px solid #eee",
                                borderRadius: 1.5,
                                p: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {type.replace(/_/g, " ")}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {createdAt ? formatTableDate(createdAt) : "-"}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color:
                                    points > 0
                                      ? "success.main"
                                      : points < 0
                                        ? "error.main"
                                        : "text.primary",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {points > 0 ? "+" : ""}
                                {points} pts
                              </Typography>
                            </Box>
                          );
                        }
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>
        <ConfirmDialog
          open={invoiceDeleteConfirmOpen}
          onClose={() => {
            setInvoiceDeleteConfirmOpen(false);
            setInvoiceToDeleteId(null);
          }}
          title="Delete invoice"
          message="Are you sure you want to delete this invoice? This action cannot be undone."
          onConfirm={async () => {
            if (invoiceToDeleteId === null) return;
            try {
              await deleteInvoice(String(invoiceToDeleteId));
              toast.success("Invoice deleted successfully!", {
                position: "top-right",
              });
              if (historyOpen && editingRow?.id) {
                fetchInvoicesForClient(editingRow.id);
              } else {
                setInvoices((prev) =>
                  prev.filter(
                    (inv) =>
                      String(inv?.id ?? inv?.invoice?.id) !==
                      String(invoiceToDeleteId)
                  )
                );
              }
            } catch (error: any) {
              console.error("Error deleting invoice:", error);
              const errorMessage =
                error?.response?.data?.error_description ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to delete invoice.";
              toast.error(errorMessage, { position: "top-right" });
            } finally {
              setInvoiceDeleteConfirmOpen(false);
              setInvoiceToDeleteId(null);
            }
          }}
        />
        <GenericFormDialog
          open={importOpen}
          onClose={() => {
            setImportOpen(false);
            resetUploadedFile();
          }}
          title="Import CSV"
          fields={importFields}
          onSubmit={(values) => {
            if (uploadedFile) {
              importCsv(uploadedFile);
            }
            setImportOpen(false);
            resetUploadedFile();
          }}
          submitLabel="Import"
        />

        <CustomDialog
          open={invoiceEditOpen}
          onClose={() => setInvoiceEditOpen(false)}
          title="Edit Invoice"
          paperSx={{
            width: { xs: "95vw", sm: "85vw", md: 850 },
            maxWidth: 850,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <Box>
                <CustomLabel text="Client Name" />
                <CustomTextField
                  value={
                    pickNonEmptyString(
                      editingInvoice?.name,
                      editingInvoice?.client?.name,
                      editingInvoice?.client?.fullName,
                      editingRow?.fullName
                    ) || ""
                  }
                  fullWidth
                  size="small"
                  disabled
                />
              </Box>
              <Box>
                <CustomLabel text="Email" />
                <CustomTextField
                  value={editingInvoice?.client.email || ""}
                  fullWidth
                  size="small"
                  disabled
                />
              </Box>
            </Box>

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <Box>
                <CustomLabel text="Phone Number" />
                <CustomTextField
                  value={
                    pickNonEmptyString(
                      editingInvoice?.mobile,
                      editingInvoice?.client?.mobile,
                      editingInvoice?.client?.phoneNumber,
                      editingInvoice?.client?.phone,
                      editingRow?.phoneNumber
                    ) || ""
                  }
                  fullWidth
                  size="small"
                />
              </Box>
              <Box>
                <CustomLabel text="Date" />
                <CustomTextField
                  type="date"
                  value={(() => {
                    const date =
                      editingInvoice?.created_at ||
                      editingInvoice?.invoice_date ||
                      editingInvoice?.invoice?.created_at ||
                      editingInvoice?.invoice?.invoice_date;
                    if (!date) return "";
                    const dateObj = new Date(date);
                    return dateObj.toISOString().split("T")[0];
                  })()}
                  fullWidth
                  size="small"
                />
              </Box>
            </Box>

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <Box>
                <CustomLabel text="Status" />
                <Select
                  value={
                    editingInvoice?.status ||
                    resolveStatusFormValue(
                      editingInvoice?.status,
                      editingInvoice?.status_id
                    )
                  }
                  fullWidth
                  size="small"
                  onChange={(event) => {
                    const value = event.target.value as
                      | "paid"
                      | "unpaid"
                      | "pending";
                    patchEditingInvoice({
                      status: value,
                      status_id: statusStringToId(value),
                    });
                  }}
                >
                  <MenuItem value="paid">paid</MenuItem>
                  <MenuItem value="unpaid">unpaid</MenuItem>
                  <MenuItem value="pending">pending</MenuItem>
                </Select>
              </Box>
              <Box>
                <CustomLabel text="Payment method" />
                <Select
                  value={resolvePaymentMethodFormValue(editingInvoice)}
                  fullWidth
                  size="small"
                  onChange={(event) => {
                    const value = event.target.value as
                      | "cash"
                      | "card"
                      | "bank transfer";
                    patchEditingInvoice({
                      payment_method_id: paymentMethodStringToId(value),
                    });
                  }}
                >
                  <MenuItem value="cash">cash</MenuItem>
                  <MenuItem value="card">card</MenuItem>
                  <MenuItem value="bank transfer">bank transfer</MenuItem>
                </Select>
              </Box>
            </Box>

            {/* Notes */}
            <Box>
              <CustomLabel text="Notes" />
              <CustomTextField
                value={editingInvoice?.notes || ""}
                fullWidth
                multiline
                rows={2}
                size="small"
                onChange={(event) =>
                  patchEditingInvoice({ notes: event.target.value })
                }
              />
            </Box>

            <InvoiceItemsEditor
              value={invoiceItems}
              onChange={(items) => setInvoiceItems(items)}
              vatValue={vat}
              onVatChange={(value) => setVat(value)}
              discountValue={discount}
              onDiscountChange={(value) => setDiscount(value)}
              isEditMode
              servicesList={services}
            />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 2,
              }}
            >
              <CustomButton
                label="Cancel"
                onClick={() => setInvoiceEditOpen(false)}
                variant="text"
                textColor={COLORS.MainColor}
              />
              <CustomButton
                label="Submit"
                onClick={async () => {
                  try {
                    if (invoiceItems.some(isInvoiceProductMissingSelection)) {
                      toast.error(
                        "Choose a product or delete the product row before saving the invoice."
                      );
                      return;
                    }

                    const servicePayload = invoiceItems.map((item: any) => ({
                      service_id: resolveServiceIdForItem(item),
                      quantity: item.quantity,
                      amount: formatAmountString(item.unitCost),
                      discount: formatAmountString(item.discount),
                      net_amount: formatAmountString(item.totalPrice),
                    }));

                    const totalAmount = servicePayload.reduce(
                      (sum: number, item: any) =>
                        sum + Number(item.net_amount || 0),
                      0
                    );

                    const discountAmount = (totalAmount * discount) / 100;
                    const amountAfterDiscount = totalAmount - discountAmount;
                    const vatAmount = (amountAfterDiscount * vat) / 100;
                    const netAmount = amountAfterDiscount + vatAmount;

                    const invoiceDate =
                      editingInvoice?.invoice_date ||
                      editingInvoice?.invoice?.invoice_date ||
                      (editingInvoice?.created_at
                        ? new Date(editingInvoice.created_at)
                            .toISOString()
                            .split("T")[0]
                        : new Date().toISOString().split("T")[0]);

                    const payload = {
                      client_id: editingInvoice?.client_id,
                      appointment_id: editingInvoice?.appointment_id || "1",
                      business_id: businessId,
                      unit_id: selectValue !== "all" ? selectValue : undefined,
                      id: editingInvoice?.id,
                      invoice_date: invoiceDate,
                      status_id:
                        editingInvoice?.status === "paid"
                          ? 1
                          : editingInvoice?.status === "unpaid"
                          ? 2
                          : 3,
                      invoice_number: editingInvoice?.invoice_number || 5967,
                      total_amount: formatAmountString(totalAmount),
                      discount: formatAmountString(discount),
                      payment_method_id: editingInvoice?.payment_method_id || 2,
                      created_by: getUserId() || "1",
                      created_at: new Date().toISOString(),
                      Vat: vat,
                      net_amount: formatAmountString(netAmount),
                      services: servicePayload,
                      notes: editingInvoice?.notes || "",
                    };

                    if (editingInvoice) {
                      const invoiceIdForUrl =
                        editingInvoice.id ||
                        editingInvoice.invoice?.id ||
                        editingInvoice.invoice_id ||
                        (Array.isArray(editingInvoice.details) &&
                        editingInvoice.details.length > 0
                          ? editingInvoice.details[0]?.invoice_id
                          : undefined);

                      if (!invoiceIdForUrl) {
                        toast.error(
                          "Unable to determine invoice ID for update.",
                          {
                            position: "top-right",
                          }
                        );
                        return;
                      }

                      await updateInvoice(String(invoiceIdForUrl), payload);
                      toast.success("Invoice updated successfully!", {
                        position: "top-right",
                      });
                    }

                    setInvoiceEditOpen(false);
                    setEditingInvoice(null);

                    // Refresh invoices
                    if (historyOpen && editingRow?.id) {
                      fetchInvoicesForClient(editingRow.id);
                    }
                  } catch (error: any) {
                    console.error("Error saving invoice:", error);
                    const errorMessage =
                      error?.response?.data?.error_description ||
                      error?.response?.data?.error ||
                      error?.message ||
                      "Failed to save invoice. Please try again.";
                    toast.error(errorMessage, {
                      position: "top-right",
                    });
                  }
                }}
                bgColor={COLORS.MainColor}
                textColor={COLORS.WhiteText}
              />
            </Box>
          </Box>
        </CustomDialog>

        <GenericFormDialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          title={`Invoice History for ${editingRow?.fullName || "Client"}`}
          customContent={
            <Box sx={{ width: "100%", maxHeight: 400, overflow: "auto" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    backgroundColor: "#f5f5f5",
                    border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                  }}
                >
                  <CustomLabel text="Total" fontWeight="600" fontSize="0.9rem" />
                  <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                    {invoiceTotals.total}
                  </Box>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    backgroundColor: "#f5f5f5",
                    border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                  }}
                >
                  <CustomLabel text="Paid" fontWeight="600" fontSize="0.9rem" />
                  <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                    {invoiceTotals.paid}
                  </Box>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    backgroundColor: "#f5f5f5",
                    border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                  }}
                >
                  <CustomLabel
                    text="Remaining"
                    fontWeight="600"
                    fontSize="0.9rem"
                  />
                  <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                    {invoiceTotals.remaining}
                  </Box>
                </Box>
              </Box>
              <CustomTable<any>
                filters={{ page: 0, size: 10 }}
                clientSidePagination={true}
                loading={loadingInvoices}
                actions={actions}
                fields={[
                  { key: "type", header: "Type" },
                  { key: "total", header: "Total" },
                  {
                    key: "paid",
                    header: "Paid",
                    transform: async (row: any) => {
                      const total = Number(row?.total) || 0;
                      const balance = Number(row?.balance) || 0;
                      const paid = total - balance;
                      return paid >= 0 ? paid.toFixed(2) : "0.00";
                    },
                  },
                  { key: "balance", header: "Remaining" },
                  {
                    key: "status",
                    header: "Status",
                    transform: async (row: any) => {
                      const status = row?.status || "";
                      const formatted = status
                        .toLowerCase()
                        .split("_")
                        .map((word: string) =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ");

                      return (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "10px",
                            backgroundColor: COLORS.MainColor,
                            color: COLORS.WhiteText,
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            textTransform: "none",
                          }}
                        >
                          {formatted || "-"}
                        </Box>
                      );
                    },
                  },
                  {
                    key: "date",
                    header: "Date",
                    transform: async (row: any) => {
                      const dateStr = row?.issuedAt || row?.createdAt;
                      if (!dateStr) return "-";
                      return formatTableDate(dateStr);
                    },
                  },
                ]}
                fetchFunc={async (params?: any) => {
                  const content = invoices;
                  const totalElements = content.length;
                  const pageSize = (params && params.size) || 10;
                  const totalPages = Math.max(
                    1,
                    Math.ceil(totalElements / pageSize)
                  );

                  return {
                    content,
                    number: 0,
                    size: pageSize,
                    totalElements,
                    totalPages,
                  };
                }}
              />
            </Box>
          }
          showSubmit={false}
        />

        <GenericFormDialog
          open={attachmentsOpen}
          onClose={() => {
            setAttachmentsOpen(false);
            setAttachments([]);
            setAttachmentTitle("");
            setAttachmentNotes("");
            resetAttachmentUploadInput();
          }}
          title={`Attachments for ${editingRow?.fullName || "Client"}`}
          customContent={
            <Box sx={{ width: "100%", minHeight: 300 }}>
              {/* Title and Notes Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <CustomLabel
                    text="Title"
                    fontSize="0.9rem"
                    fontWeight="600"
                    sx={{ mb: 1 }}
                  />
                  <CustomTextField
                    value={attachmentTitle}
                    onChange={(e) => setAttachmentTitle(e.target.value)}
                    placeholder="Enter attachment title"
                    fullWidth
                    size="small"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <CustomLabel
                    text="Notes"
                    fontSize="0.9rem"
                    fontWeight="600"
                    sx={{ mb: 1 }}
                  />
                  <CustomTextField
                    value={attachmentNotes}
                    onChange={(e) => setAttachmentNotes(e.target.value)}
                    placeholder="Enter notes or description"
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                </Box>
              </Box>

              {/* File Upload Section */}
              <Box sx={{ mb: 3 }}>
                <CustomLabel
                  text="Upload Documents"
                  fontSize="1rem"
                  fontWeight="600"
                  sx={{ mb: 2 }}
                />
                <CustomUpload
                  key={attachmentUploadKey}
                  label="Choose files to upload"
                  onChange={(files) => {
                    if (files && files.length > 0) {
                      setAttachments((prev) => [...prev, ...Array.from(files)]);
                      toast.success(`${files.length} file(s) added`);
                    }
                  }}
                  multiple
                />
              </Box>

              {/* Pending Upload Files */}
              {attachments.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <CustomLabel
                    text="Ready to Upload"
                    fontSize="0.95rem"
                    fontWeight="600"
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {attachments.map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1.5,
                          backgroundColor: "#fff3e0",
                          borderRadius: "8px",
                          border: "1px solid #ffb74d",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
                        >
                          <AttachFileIcon
                            sx={{ color: "#ff9800", fontSize: 20 }}
                          />
                          <Box>
                            <Box sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
                              {file.name}
                            </Box>
                            <Box sx={{ fontSize: "0.75rem", color: "#666" }}>
                              {(file.size / 1024).toFixed(2)} KB
                            </Box>
                          </Box>
                        </Box>
                        <CustomButton
                          label="Remove"
                          variant="text"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            setAttachments((prev) =>
                              prev.filter((_, i) => i !== index)
                            );
                            toast.info("File removed from queue");
                            resetAttachmentUploadInput();
                          }}
                          sx={{
                            color: "error.main",
                            minWidth: "auto",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <CustomButton
                      label="Save Attachments"
                      onClick={async () => {
                        if (!editingRow?.id) {
                          toast.error("No client selected");
                          return;
                        }

                        if (attachments.length === 0) {
                          toast.warning("No files to upload");
                          return;
                        }

                        if (!attachmentTitle.trim()) {
                          toast.error("Please enter a title");
                          return;
                        }

                        try {
                          for (const file of attachments) {
                            await createAttachment(
                              editingRow.id,
                              file,
                              attachmentTitle,
                              attachmentNotes
                            );
                          }
                          toast.success("Attachments saved successfully!");
                          setAttachments([]);
                          setAttachmentTitle("");
                          setAttachmentNotes("");
                          fetchAttachmentsForClient(editingRow.id);
                          resetAttachmentUploadInput();
                        } catch (error: any) {
                          console.error("Error saving attachments:", error);
                          const errorMessage =
                            error?.response?.data?.error_description ||
                            error?.response?.data?.error ||
                            error?.message ||
                            "Failed to save attachments.";
                          toast.error(errorMessage);
                        }
                      }}
                      bgColor={COLORS.MainColor}
                      textColor={COLORS.WhiteText}
                    />
                  </Box>
                </Box>
              )}

              {/* Existing Documents List Section */}
              <Box
                sx={{
                  border: `2px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                  borderRadius: "12px",
                  p: 2,
                  minHeight: 200,
                  maxHeight: 400,
                  overflow: "auto",
                  backgroundColor: "#fafafa",
                }}
              >
                <CustomLabel
                  text="Saved Documents"
                  fontSize="0.95rem"
                  fontWeight="600"
                  sx={{ mb: 2 }}
                />

                {loadingAttachments ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 150,
                      color: "#999",
                      fontSize: "0.9rem",
                    }}
                  >
                    Loading attachments...
                  </Box>
                ) : existingAttachments.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 150,
                      color: "#999",
                      fontSize: "0.9rem",
                    }}
                  >
                    No documents saved yet
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {existingAttachments.map((attachment, index) => (
                      <Box
                        key={attachment.id || index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1.5,
                          backgroundColor: "white",
                          borderRadius: "8px",
                          border: "1px solid #e0e0e0",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <Box
                          onClick={() => {
                            const fileUrl = getAttachmentFileUrl(attachment);
                            if (fileUrl) {
                              window.open(fileUrl, "_blank", "noopener,noreferrer");
                            } else {
                              toast.error("File not found");
                            }
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flex: 1,
                            cursor: "pointer",
                            "&:hover": {
                              opacity: 0.8,
                            },
                          }}
                        >
                          <AttachFileIcon
                            sx={{ color: COLORS.activeBackground, fontSize: 20 }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
                              {attachment.title || "Untitled"}
                            </Box>
                            <Box sx={{ fontSize: "0.75rem", color: "#888", mt: 0.3 }}>
                              File: {getAttachmentDisplayName(attachment)}
                            </Box>
                            {attachment.notes && (
                              <Box sx={{ fontSize: "0.75rem", color: "#666", mt: 0.5 }}>
                                {attachment.notes}
                              </Box>
                            )}
                            <Box sx={{ fontSize: "0.7rem", color: "#999", mt: 0.5 }}>
                              {attachment.created_at
                                ? formatTableDate(attachment.created_at)
                                : ""}
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <CustomButton
                            label="Download"
                            variant="text"
                            startIcon={<FileDownloadIcon />}
                            onClick={async () => {
                              const downloaded = await triggerAttachmentDownload(
                                attachment
                              );
                              if (downloaded) {
                                toast.success("Download started");
                                return;
                              }

                              const opened = openAttachmentInNewTab(attachment);
                              if (opened) {
                                toast.info("Opened file in new tab");
                              } else {
                                toast.error("File not found");
                              }
                            }}
                            textColor={COLORS.MainColor}
                            sx={{
                              minWidth: "auto",
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.04)",
                              },
                            }}
                          />
                          <CustomButton
                            label="Delete"
                            variant="text"
                            startIcon={<DeleteIcon />}
                            onClick={async () => {
                              if (!attachment.id) {
                                toast.error("Invalid attachment ID");
                                return;
                              }

                              try {
                                await deleteAttachment(attachment.id);
                                toast.success("Attachment deleted successfully!");
                                fetchAttachmentsForClient(editingRow?.id);
                              } catch (error: any) {
                                console.error("Error deleting attachment:", error);
                                const errorMessage =
                                  error?.response?.data?.error_description ||
                                  error?.response?.data?.error ||
                                  error?.message ||
                                  "Failed to delete attachment.";
                                toast.error(errorMessage);
                              }
                            }}
                            sx={{
                              color: "error.main",
                              minWidth: "auto",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.04)",
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          }
          showSubmit={false}
        />
      </Box>
      <AddClientDialog
        open={addClientDialogOpen}
        onClose={() => setAddClientDialogOpen(false)}
        branches={branches}
        initialBranch={
          selectValue !== "all" ? String(selectValue || "") : null
        }
        onCreated={() => {
          setAddClientDialogOpen(false);
          setRefetchTrigger((prev) => prev + 1);
        }}
      />
    </Box>
  );
};

export default Clients;
